import type React from "react";
import type {RouteKey} from "~/routing/routes.ts";
import BarChartIcon from '../icons/svg/BarChartIcon.svg?react';
import DatabaseIcon from '../icons/svg/DatabaseIcon.svg?react';
import DeliveryBoxIcon from '../icons/svg/DeliveryBoxIcon.svg?react';
import FlowChartIcon from '../icons/svg/FlowChartIcon.svg?react';
import LineChartIcon from '../icons/svg/LineChartIcon.svg?react';
import PieChartIcon from '../icons/svg/PieChartIcon.svg?react';
import SearchBoxIcon from '../icons/svg/SearchBoxIcon.svg?react';
import StoreIcon from '~/icons/svg/StoreIcon.svg?react';
import AutoCategorizationIcon from '../icons/svg/AutoCategorizationIcon.svg?react';
import BotFlowIcon from '../icons/svg/BotFlowIcon.svg?react';
import AtlasIcon from '../icons/svg/AtlasIcon.svg?react';
import DashboardIcon from "../icons/svg/DashboardIcon.svg?react";
import SavingsPigIcon from '../icons/svg/SavingsPigIcon.svg?react';
import {AccountBalanceWalletOutlined, AddLinkOutlined, CategoryOutlined, HandshakeOutlined, PaymentsOutlined} from "@mui/icons-material";
import ChatConstants from "../../pages/chat-agent/ChatConstants.ts";
import type {DashboardSection} from "~/components/dashboard/DashboardSectionConfig.tsx";
import {B} from "~/build.ts";
import {environment} from "~/env.ts";

export type CardConfig = {
    /**
     * The route to which the card navigates when clicked.
     * If false the link will not work.
     */
    route: RouteKey
    demoRoute?: RouteKey
    routePrefix?: string
    title: string
    icon?: React.ReactNode

    section: DashboardSection
    ordering: number

    /**
     * Card that require activation are not available by default
     * They need to be explicitly enabled the environment file
     */
    defaultHidden?: boolean

    /**
     * Card that will not be available for sample data bags
     */
    sampleDisabled?: boolean

    cardDescription?: React.ReactNode

    tag?: string

    /**
     * Keep the menu item highlighted on sub-pages of the route (e.g. a module's tab URLs)
     */
    matchSubpaths?: boolean
}

function tagValueExceptForDemo<T>(value: T): T | undefined {
    if (
        B.APP_CUSTOMER === 'meet-mithra'
        || B.DISABLE_ALL_CARD_TAGS
        || environment.isDemo
    ) return undefined;
    return value;
}

const cardConfigs = {
    // Mithra 3.0 Explore Categories + Category Profile (CAT-2191). Hidden unless a configuration lists it in
    // `showCards`: it needs the experimental `category_profile` backend app for that customer (cat-material
    // `category_profile/__EXPERIMENTAL__.yaml`).
    'category-explore': {
        defaultHidden: true,
        section: 'insights_2',
        title: 'Category Profile',
        route: 'category_explore',
        icon: <CategoryOutlined sx={{color: '#44474F'}}/>,
        cardDescription: 'Every category from L1 to leaf, with its spend, suppliers, strategy, benchmarks and market drivers',
        sampleDisabled: true,
        matchSubpaths: true,
        ordering: 105,
    },
    'koi-key-data-facts': {
        section: 'insights_2',
        title: 'Explore Categories',
        route: 'analytics_koi_kdf',
        icon: <SearchBoxIcon/>,
        cardDescription: 'Get a top-level visibility into category spend',
        sampleDisabled: true,
        ordering: 110,
    },
    'categorization-review': {
        section: 'data_1',
        title: 'Auto Categorization',
        route: 'cleanup_categorization',
        icon: <AutoCategorizationIcon/>,
        cardDescription: 'Auto-categorize spend with high accuracy',
        ordering: 320,
    },
    'contract-explorer': {
        defaultHidden: true,
        section: 'insights_2',
        title: 'Contract Explorer',
        tag: tagValueExceptForDemo('trial'),
        route: 'contract_explorer',
        icon: <AddLinkOutlined sx={{color: '#44474F'}}/>,
        cardDescription: 'Review AI-proposed links between contracts and spend transactions',
        ordering: 325,
    },
    'taxonomy-builder': {
        section: 'data_1',
        title: 'Taxonomy Builder',
        route: 'cleanup_taxonomy_builder',
        icon: <FlowChartIcon/>,
        cardDescription: 'Create and adjust spend taxonomies with full control',
        ordering: 311,
    },
    'koi-ppv': {
        section: 'insights_2',
        title: 'Purchase Price Variances',
        route: 'analytics_koi_ppv',
        icon: <LineChartIcon/>,
        cardDescription: 'Identify spend fragmentation across all categories',
        sampleDisabled: true,
        ordering: 430,
    },
    'koi-spend-concentration': {
        section: 'insights_2',
        title: 'Spend Concentration',
        route: 'analytics_koi_sc',
        icon: <BarChartIcon/>,
        cardDescription: 'Identify spend fragmentation across all categories',
        sampleDisabled: true,
        ordering: 420,
    },
    'koi-spend-segmentation': {
        section: 'no_section',
        title: 'Spend Segmentation',
        route: 'analytics_koi_ss',
        // icon: null,
        defaultHidden: true,
        ordering: 298,
    },
    'supplier-360': {
        section: 'data_1',
        title: 'Supplier 360',
        route: 'supplier_360',
        icon: <StoreIcon/>,
        cardDescription: 'A centralized, enriched profile for every supplier',
        sampleDisabled: true,
        ordering: 330,
    },
    'adv-dashboard': {
        section: 'insights_2',
        title: 'Spend Landscape',
        route: 'adv_dashboard',
        icon: <PieChartIcon/>,
        cardDescription: 'Explore spend across categories, suppliers and regions',
        sampleDisabled: true,
        ordering: 400,
    },
    'payment-terms': {
        defaultHidden: true,
        section: 'insights_2',
        title: 'Payment Terms Optimisation',
        tag: tagValueExceptForDemo('trial'),
        route: 'payment_terms',
        icon: <AccountBalanceWalletOutlined sx={{color: '#44474F'}}/>,
        cardDescription: 'Unlock working capital by aligning supplier payment terms with policy',
        ordering: 410,
    },
    'payment-terms-v2': {
        // Visible in local dev builds; deployed tenants enable it through `showCards`
        defaultHidden: !B.APP_IS_DEV,
        matchSubpaths: true,
        section: 'insights_2',
        title: 'Payment Terms',
        route: 'payment_terms_v2',
        icon: <PaymentsOutlined sx={{color: '#44474F'}}/>,
        cardDescription: 'Release working capital trapped by payment term leakage and short payment terms',
        ordering: 411,
    },
    'taxonomy-mapper': {
        section: 'no_section',
        title: 'Taxonomy Mapper',
        route: 'demo_merge_taxonomy_mapper',
        // icon: null,
        defaultHidden: true,
        ordering: 299,
    },
    'taxonomy-suggester': {
        section: 'data_1',
        title: 'Taxonomy Suggester',
        route: 'demo_cleanup_taxonomy_suggestion_v2',
        icon: <BotFlowIcon/>,
        cardDescription: 'AI suggests taxonomy based on your data and industry benchmarks',
        defaultHidden: true,
        ordering: 310,
    },
    'contract-coverage-intelligence': {
        section: 'insights_2',
        title: 'Contract Coverage Intelligence',
        route: 'contract_coverage_intelligence',
        icon: <HandshakeOutlined sx={{color: '#44474F'}}/>,
        cardDescription: 'Surface contract positions, cost saving and cost avoidance across your supplier base',
        defaultHidden: true,
        ordering: 415,
    },
    'golden-record': {
        section: B.APP_CUSTOMER === 'meet-mithra' ? 'insights_2' : 'data_1',
        title: 'Material Harmonization',
        route: 'cleanup_golden_record',
        tag: tagValueExceptForDemo('trial'),
        icon: <DeliveryBoxIcon/>,
        cardDescription: 'Identify & consolidate similar materials across all sources',
        defaultHidden: true,
        sampleDisabled: true,
        ordering: 350,
    },
    'chat': {
        section: 'chat',
        title: ChatConstants.name,
        route: 'chat',
        icon: <AtlasIcon/>,
        defaultHidden: true,
        sampleDisabled: true,
        ordering: 300,
    },
    'dashboards': {
        // Main menu, right after Atlas (Mithra 3.0 nav: Atlas | Dashboards).
        section: 'chat',
        title: 'Dashboards',
        route: 'dashboards',
        icon: <DashboardIcon/>,
        cardDescription: 'Built-in dashboards maintained by Mithra and the ones you build with Atlas',
        defaultHidden: true,
        sampleDisabled: true,
        ordering: 305,
    },
    'sit': {
        section: 'sit',
        title: 'Savings Initiative Tracker',
        route: 'sit_home',
        routePrefix: '/savings-tracker',
        icon: <SavingsPigIcon/>,
        defaultHidden: true,
        ordering: 230,
    },
    'data-ingestion': {
        section: 'no_section',
        title: 'Data Ingestion',
        route: 'data_ingestion',
        demoRoute: 'data_upload',
        icon: <DatabaseIcon/>,
        defaultHidden: true,
        ordering: 210,
    },
    'taxonomy-ingestion': {
        section: 'no_section',
        title: 'Taxonomy Ingestion',
        route: 'taxonomy_ingestion',
        icon: <DatabaseIcon/>,
        defaultHidden: true,
        ordering: 220,
    },
} satisfies Record<string, CardConfig>

export type DashboardCard = keyof typeof cardConfigs; // extract keys
export const CARD_CONFIG: Record<DashboardCard, CardConfig> = cardConfigs; // expose type
